import { useMemo } from 'react'

import { IndividualType } from '@/hooks/useIndividuals'

import { useDraftDocumentsLiveSync } from './useDraftDocumentsLiveSync'

/**
 * A document whose non-terminal status hasn't moved in this long is treated as
 * stuck, not in flight.
 *
 * Without this bound, one document abandoned mid-pipeline (a failed upload left
 * at `waiting_for_upload`, a worker that died before writing a terminal status)
 * would suppress the regen offer forever — the backend would never write to its
 * RTDB node again, so nothing could ever clear it. Mirrors the backend's own
 * `REGEN_STUCK_MS` reasoning: after long enough, assume nobody is coming.
 */
const STUCK_AFTER_MS = 30 * 60 * 1000

/**
 * Document statuses that mean "the AI pipeline still has work to do on this
 * document, so the individual's knowledge base is incomplete".
 *
 * `draft` is deliberately absent: a draft is not committed and never reaches the
 * pipeline, so a draft the user abandoned would otherwise count as in-flight
 * forever. `done` / `error` are terminal.
 */
const PROCESSING_STATUSES = [
  'waiting_for_upload',
  'uploaded',
  'parsing',
  'analyzing',
  'embedding',
] as const

type ProcessingStatus = (typeof PROCESSING_STATUSES)[number]

const isProcessingStatus = (status: string): status is ProcessingStatus =>
  (PROCESSING_STATUSES as readonly string[]).includes(status)

/**
 * Documents of this individual that are still moving through the pipeline.
 *
 * Also opens an RTDB subscription per in-flight document (via the existing
 * live-sync hook) so the individual query is refetched the moment a document
 * reaches a terminal status. Without it the count would only shrink on
 * navigation or reload, and anything gated on it would stay hidden.
 */
export const useProcessingDocuments = (individual?: IndividualType) => {
  const processingIds = useMemo(() => {
    const stuckBefore = Date.now() - STUCK_AFTER_MS

    return (individual?.documents ?? [])
      .filter((d) => {
        if (!isProcessingStatus(d.status)) return false
        const movedAt = Date.parse(d.updatedAt ?? d.createdAt)

        return Number.isNaN(movedAt) || movedAt > stuckBefore
      })
      .map((d) => d.id)
  }, [individual?.documents])

  useDraftDocumentsLiveSync(individual?.id, processingIds)

  return { isProcessing: processingIds.length > 0 }
}
